import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import M from '@materializecss/materialize';
import { logOut, registerRoutes } from '../../../func';
import { useRouteMatch } from 'react-router-dom/cjs/react-router-dom.min';
import Axios from 'axios';
import { useDispatch } from 'react-redux';

const AddressModal = ({ isOpen, setIsOpen, refresh, address, pharmacyId }) => {
  const { path } = useRouteMatch();
  const dispatch = useDispatch();
  const authData = useMemo(() => JSON.parse(localStorage.getItem('auth_data')));

  const modal = useRef(null);
  const addressTypes = () => ['physical', 'mailing', 'billing', 'other'];

  const initValues = () => ({
    addressLineOne: '',
    addressLineTwo: '',
    addressLineThree: '',
    city: '',
    stateId: '',
    postalCode: '',
    addressType: 'physical',
  });

  const [values, setValues] = useState(initValues());
  const [stateList, setStateList] = useState([]);

  const networkCalls = useMemo(
    () =>
      registerRoutes(
        {
          getStates: {
            func: () => {
              Axios.get('/api/v1/state/read/all', {
                params: authData,
              })
                .then(result => setStateList(result.data))
                .catch(logOut);
            },
            type: 'r',
          },
          createAddress: {
            func: payload => {
              Axios.post('/api/v2/pharmacyaddress/create/one', {
                ...authData,
                ...payload,
              })
                .then(result => {
                  refresh();
                  dispatch(result.data);
                  closeModal();
                })
                .catch(logOut);
            },
            type: 'c',
          },
          updateAddress: {
            func: payload => {
              Axios.put('/api/v2/pharmacyaddress/update/one', {
                ...authData,
                ...payload,
              })
                .then(result => {
                  refresh();
                  dispatch(result.data);
                  closeModal();
                })
                .catch(logOut);
            },
            type: 'u',
          },
        },
        path
      ),
    [path]
  );

  useEffect(() => {
    networkCalls.getStates();
  }, []);

  useLayoutEffect(() => {
    if (!modal.current) return;
    M.Modal.init(modal.current, {
      onCloseEnd: () => setIsOpen(false),
      onOpenEnd: () => M.updateTextFields(),
    });

    return () => {
      M.Modal.getInstance(modal.current)?.destroy();
    };
  }, [modal.current]);

  const getModal = () =>
    modal.current ? M.Modal.getInstance(modal.current) : null;
  const openModal = () => getModal()?.open();
  const closeModal = () => getModal()?.close();

  useEffect(() => {
    isOpen ? openModal() : closeModal();
  }, [isOpen]);

  useEffect(() => {
    if (!address) return setValues(initValues());

    setValues({
      addressLineOne: address.addressLineOne,
      addressLineTwo: address.addressLineTwo,
      addressLineThree: address.addressLineThree,
      city: address.city,
      stateId: address.stateId,
      postalCode: address.postalCode,
      addressType: address.addressType,
    });
  }, [address]);

    useLayoutEffect(() => {
    if (!modal.current || !isOpen) return;
    M.FormSelect.init(modal.current.querySelectorAll('select'));
    return () => {
      modal.current.querySelectorAll('select')?.forEach(el => {
        if (el) M.FormSelect.getInstance(el)?.destroy();
      });
    };
  }, [stateList, values.stateId, values.addressType, isOpen]);

  const onChange = ({ target: { id, value, type } }) => {
    if (type.toLocaleLowerCase === 'checkbox')
      return setValues(prev => ({ ...prev, [id]: !prev[id] }));

    setValues(prev => ({ ...prev, [id]: value }));
  };

  const save = e => {
    e?.preventDefault();
    if (address)
      return networkCalls.updateAddress({
        pharmacyAddressId: address.id,
        ...values,
      });
    networkCalls.createAddress({
      pharmacyId,
      ...values,
    });
  };

  const cancel = e => {
    e.preventDefault();
    closeModal();
  };

  return (
    <div ref={modal} className="modal">
      <div className="modal-content">
        <div className="row">
          <h5 className="center">{address ? 'Edit' : 'Add'} Address</h5>
          <div className="divider" />
        </div>
        <div className="row">
          <div className="input-field col s12">
            <input
              id="addressLineOne"
              type="text"
              value={values.addressLineOne}
              onChange={onChange}
            />
            <label htmlFor="addressLineOne">
              Address Line One (Name as it appears on address)
            </label>
          </div>
          <div className="input-field col s12">
            <input
              id="addressLineTwo"
              type="text"
              value={values.addressLineTwo}
              onChange={onChange}
            />
            <label htmlFor="addressLineTwo">Address Line Two</label>
          </div>
          <div className="input-field col s12">
            <input
              id="addressLineThree"
              type="text"
              value={values.addressLineThree}
              onChange={onChange}
            />
            <label htmlFor="addressLineThree">Address Line Three</label>
          </div>
          <div className="input-field col s12 m6">
            <input
              id="city"
              type="text"
              value={values.city}
              onChange={onChange}
            />
            <label htmlFor="city">City</label>
          </div>
          <div className="input-field col s12 m6">
            <select id="stateId" value={values.stateId} onChange={onChange}>
              <option value=""></option>
              {stateList?.map(({ id, name }) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </select>
            <label htmlFor="stateId">State</label>
          </div>
          <div className="input-field col s12 m3">
            <input
              id="postalCode"
              type="text"
              value={values.postalCode}
              onChange={onChange}
            />
            <label htmlFor="postalCode">Postal Code</label>
          </div>
          <div className="input-field col s12 m4">
            <select
              id="addressType"
              value={values.addressType}
              onChange={onChange}
            >
              {addressTypes().map(type => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
            <label htmlFor="addressType">Address Type</label>
          </div>
        </div>
        <div className="row">
          <div className="col s12 m4 offset-m4">
            <a
              href="/"
              className="btn-small blue white-text waves-effect waves-light col s12"
              onClick={save}
            >
              {address ? 'Update' : 'Save'}
            </a>
          </div>
          <div className="col s12 m4">
            <a
              href="/"
              className="btn-small white blue-text waves effect waves-dark col s12"
              onClick={cancel}
            >
              Cancel
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddressModal;
